import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Eye, EyeOff, Building2 } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { useBranding } from '../../contexts/BrandingProvider'
import { publicOrgApi } from '../../services/api/publicOrg'
import { PublicOrg } from '../../types/branding'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Label } from '../../components/ui/Label'
import { LoadingSpinner } from '../../components/ui/LoadingSpinner'
import { Logo } from '../../components/ui/Logo'
import toast from 'react-hot-toast'

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
})

type LoginFormData = z.infer<typeof loginSchema>

export const SlugLoginPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const { login, isLoading: authLoading } = useAuth()
  const { applyTemporary } = useBranding()
  const [org, setOrg] = useState<PublicOrg | null>(null)
  const [fetchError, setFetchError] = useState<'not_found' | 'error' | null>(null)
  const [fetchLoading, setFetchLoading] = useState(true)
  const [showPassword, setShowPassword] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  })

  useEffect(() => {
    if (!slug) {
      setFetchError('not_found')
      setFetchLoading(false)
      return
    }
    sessionStorage.setItem('branded_slug', slug)
    let cancelled = false
    setFetchLoading(true)
    publicOrgApi.getBySlug(slug)
      .then((result) => {
        if (cancelled) return
        setOrg(result)
        applyTemporary(result)
      })
      .catch((err: Error & { status?: number }) => {
        if (cancelled) return
        setFetchError(err.status === 404 ? 'not_found' : 'error')
      })
      .finally(() => {
        if (!cancelled) setFetchLoading(false)
      })
    return () => { cancelled = true }
  }, [slug, applyTemporary])

  const onSubmit = async (data: LoginFormData) => {
    try {
      await login(data.email, data.password)
      toast.success('Welcome back!')
      navigate(`/o/${slug}/app/dashboard`, { replace: true })
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Login failed. Please try again.')
    }
  }

  if (fetchLoading) {
    return (
      <div
        className="min-h-screen bg-background flex items-center justify-center"
        aria-label="Loading organization"
      >
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (fetchError === 'not_found') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="max-w-sm w-full text-center space-y-4">
          <div className="flex justify-center">
            <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center">
              <Building2 className="h-6 w-6 text-muted-foreground" />
            </div>
          </div>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">
            Organization not found
          </h1>
          <p className="text-sm text-muted-foreground">
            The organization at this link does not exist or is no longer active.
          </p>
          <Link to="/login">
            <Button variant="outline" className="mt-2">
              Back to login
            </Button>
          </Link>
        </div>
      </div>
    )
  }

  if (fetchError === 'error') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="max-w-sm w-full text-center space-y-4">
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">
            Something went wrong
          </h1>
          <p className="text-sm text-muted-foreground">
            Unable to load this organization. Please try again later.
          </p>
          <Link to="/login">
            <Button variant="outline" className="mt-2">
              Back to login
            </Button>
          </Link>
        </div>
      </div>
    )
  }

  const orgLogoUrl = org?.logoUrl ?? null
  const orgName = org?.name ?? ''

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center space-y-3">
          {orgLogoUrl ? (
            <div className="mx-auto inline-flex items-center justify-center bg-card rounded-xl px-4 py-3 border border-border">
              <img
                src={orgLogoUrl}
                alt={orgName}
                className="h-12 w-auto object-contain"
              />
            </div>
          ) : (
            <div className="mx-auto h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
              <Building2 className="h-6 w-6 text-primary" />
            </div>
          )}
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            1031 Exchange Platform
          </p>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">
            Sign in to {orgName}
          </h1>
        </div>

        <section
          aria-label="Sign-in form panel"
          className="bg-card border border-border rounded-2xl shadow-xl p-8 space-y-5"
        >
          <form
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            className="space-y-5"
          >
            <div className="space-y-1.5">
              <Label htmlFor="slug-email">Email address</Label>
              <Input
                {...register('email')}
                id="slug-email"
                type="email"
                autoComplete="email"
                placeholder="you@example.com"
                aria-invalid={errors.email ? 'true' : 'false'}
                aria-describedby={errors.email ? 'slug-email-error' : undefined}
              />
              {errors.email && (
                <p id="slug-email-error" role="alert" className="text-sm text-destructive">
                  {errors.email.message}
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="slug-password">Password</Label>
              <div className="relative">
                <Input
                  {...register('password')}
                  id="slug-password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="Enter your password"
                  className="pr-10"
                  aria-invalid={errors.password ? 'true' : 'false'}
                  aria-describedby={errors.password ? 'slug-password-error' : undefined}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute inset-y-0 right-0 h-full w-10"
                  onClick={() => setShowPassword((prev) => !prev)}
                  title={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Eye className="h-4 w-4 text-muted-foreground" />
                  )}
                </Button>
              </div>
              {errors.password && (
                <p id="slug-password-error" role="alert" className="text-sm text-destructive">
                  {errors.password.message}
                </p>
              )}
            </div>

            <div className="flex justify-end">
              <Link
                to={`/o/${slug}/forgot-password`}
                className="text-sm font-medium text-accent dark:text-white hover:underline underline-offset-4 hover:dark:text-white/80"
              >
                Forgot your password?
              </Link>
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={authLoading}
            >
              {authLoading ? (
                <LoadingSpinner size="sm" variant="icon" className="mr-2" />
              ) : null}
              Sign in
            </Button>
          </form>

          <p className="text-center text-sm text-muted-foreground">
            Not your organization?{' '}
            <Link
              to="/login"
              className="font-medium text-accent dark:text-white hover:underline underline-offset-4 hover:dark:text-white/80"
              onClick={() => sessionStorage.removeItem('branded_slug')}
            >
              Sign in with Open1031
            </Link>
          </p>
        </section>

        <div className="flex items-center justify-center gap-1.5 pt-4">
          <span className="text-xs text-muted-foreground/80">Powered by</span>
          <Logo className="h-5 w-auto" alt="Open1031" forceDefault />
        </div>
      </div>
    </div>
  )
}
